import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, MONTHS_ES, formatMoney } from "../lib/api";
import { ArrowLeft, CheckCircle, GitBranch, Clock, GitMerge, User, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";

export default function Versions() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const d = await api.listVersions(planId);
      setData(d);
    } catch (e) {
      toast.error("Error al cargar versiones");
    }
  };

  useEffect(() => { load(); }, [planId]);

  const handleSetCurrent = async (vn) => {
    try {
      await api.setCurrentVersion(planId, vn);
      toast.success(`V${vn} marcada como vigente`);
      load();
    } catch (e) {
      toast.error("Error al cambiar versión vigente");
    }
  };

  if (!data) return <div className="p-6">Cargando…</div>;
  const { plan, versions } = data;

  return (
    <div>
      <div className="border-b border-border bg-card px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(`/plans/${planId}`)} data-testid="back-btn" className="p-2 rounded-sm hover:bg-muted">
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="label-xs text-muted-foreground">
              {MONTHS_ES[plan.month]} · {plan.year} · {plan.name}
            </div>
            <h1 className="font-heading text-xl font-extrabold tracking-tight">Historial de Versiones</h1>
          </div>
        </div>
        <Link to={`/plans/${planId}/compare`}>
          <Button data-testid="go-to-compare-btn" className="rounded-sm font-semibold">
            <GitMerge size={16} className="mr-2" weight="bold" /> Comparar versiones
          </Button>
        </Link>
      </div>

      <div className="p-6">
        <div className="relative pl-6">
          <div className="absolute left-2 top-0 bottom-0 w-px bg-border"></div>
          {versions.slice().reverse().map((v) => (
            <div key={v.version_number} className="relative mb-6" data-testid={`version-item-${v.version_number}`}>
              <div className={`absolute -left-[22px] top-3 w-4 h-4 rounded-full border-2 ${v.is_current ? "bg-primary border-primary" : "bg-background border-border"}`}></div>
              <div className={`card-elevated p-5 ${v.is_current ? "border-foreground" : ""}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading text-2xl font-extrabold tracking-tight">V{v.version_number}</span>
                      {v.is_current && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-primary text-primary-foreground label-xs">
                          <CheckCircle size={11} weight="fill" /> Vigente
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-sm">{v.change_description}</p>
                    <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1"><Clock size={12} /> {new Date(v.created_at).toLocaleString("es-ES")}</span>
                      <span className="flex items-center gap-1"><User size={12} /> {v.author}</span>
                      <span className="flex items-center gap-1"><FileText size={12} /> {v.entry_count} partidas</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="label-xs text-muted-foreground">Saldo final proyectado</div>
                    <div className={`font-heading font-black text-xl font-mono-tabular tracking-tight mt-1 ${v.totals.final_balance >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
                      {formatMoney(v.totals.final_balance)}
                    </div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t border-border">
                  <MiniStat label="Saldo inicial" value={formatMoney(v.initial_balance)} />
                  <MiniStat label="Ingresos" value={formatMoney(v.totals.total_income)} positive />
                  <MiniStat label="Egresos" value={formatMoney(v.totals.total_expense)} negative />
                  <MiniStat label="Neto" value={formatMoney(v.totals.net_cashflow)} />
                </div>
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  <Link to={`/plans/${planId}?v=${v.version_number}`}>
                    <Button variant="outline" size="sm" className="rounded-sm" data-testid={`view-v-${v.version_number}`}>
                      Ver detalle
                    </Button>
                  </Link>
                  {!v.is_current && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm"
                      data-testid={`set-current-v-${v.version_number}`}
                      onClick={() => handleSetCurrent(v.version_number)}
                    >
                      <GitBranch size={13} className="mr-1" /> Marcar como vigente
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, positive, negative }) {
  const cls = positive ? "text-emerald-600 dark:text-emerald-400" : negative ? "text-red-600 dark:text-red-400" : "";
  return (
    <div>
      <div className="label-xs text-muted-foreground">{label}</div>
      <div className={`font-mono-tabular font-semibold mt-0.5 ${cls}`}>{value}</div>
    </div>
  );
}
